import { api, getApiBaseUrl, getAuthToken } from './apiClient';

/**
 * ANÁLISE IA — Matriz de Responsabilidade Allodos (seções 2 a 5; a seção 1,
 * transcrição literal, é o material do próprio profissional).
 */
export type SessionAnalysisApiResponse = {
  approach: string;
  /** 2. Panorama da Sessão (factual, sem linha teórica). */
  sessionOverview: {
    complaint: string;
    contextGaps: string[];
    setting: string;
    relationalClimate: string;
  };
  /** 3. Leitura Teórica (vinculada à abordagem). */
  theoreticalReading: string;
  /** 4. Manejo Técnico (vinculado à abordagem). */
  technicalManagement: string[];
  /** 5. Pontos de Atenção e Risco (vinculados à abordagem). */
  attentionAndRisk: string[];
  confidence: number;
  disclaimer: string;
};

export type ChatApiResponse = {
  answer: string;
  supportingPoints: string[];
  riskFlags: string[];
  followUpQuestions: string[];
  disclaimer: string;
};

export type CycleInterpretationResponse = {
  clinicalInsight: string;
  patientReport: string;
  evolutionGuidelines: string;
};

export async function registerAiConsent(patientId: string): Promise<void> {
  await api.post('/ai/consent', { patientId, aiClinicalSupport: true });
}

export type TranscriptionResult = {
  text: string;
  transcriptionId?: string;
};

export async function transcribeAudio(patientId: string, audioFile: File, recordingId?: string): Promise<TranscriptionResult> {
  console.info('[aiApi.transcribeAudio] start', {
    patientId,
    fileName: audioFile?.name,
    fileType: audioFile?.type,
    fileSize: audioFile?.size,
  });
  const token = getAuthToken();
  if (!token) throw new Error('Sessão expirada. Faça login novamente.');

  const form = new FormData();
  form.append('patientId', patientId);
  if (recordingId) form.append('recordingId', recordingId);
  form.append('audio', audioFile, audioFile.name || 'audio.webm');

  const res = await fetch(`${getApiBaseUrl()}/ai/transcribe-audio`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });

  const text = await res.text();
  console.info('[aiApi.transcribeAudio] response', {
    status: res.status,
    ok: res.ok,
    bodyPreview: text.slice(0, 280),
  });
  if (!res.ok) {
    let message = `Erro ${res.status}`;
    try {
      const json = JSON.parse(text) as { message?: string };
      message = json.message || message;
    } catch {
      if (text) message = text.slice(0, 220);
    }
    throw new Error(message);
  }

  try {
    const json = JSON.parse(text) as { text?: string; transcriptionId?: string };
    if (!json.text?.trim()) throw new Error();
    return { text: json.text.trim(), transcriptionId: json.transcriptionId };
  } catch {
    throw new Error('Transcrição inválida retornada pela API.');
  }
}

export async function analyzeSessionText(
  patientId: string,
  transcriptText: string,
  approach: string,
  context?: string,
  transcriptionId?: string,
): Promise<SessionAnalysisApiResponse> {
  return api.post<SessionAnalysisApiResponse>(
    '/ai/analyze-session',
    transcriptionId
      ? { patientId, transcriptionId, approach, context }
      : { patientId, transcriptText, approach, context },
  );
}

/** SUPERVISIONAR — Matriz de Responsabilidade Allodos (4 seções). */
export type SupervisionApiResponse = {
  approach: string;
  /** 1. Síntese e Fenômenos Clínicos (sintético, sem linha teórica). */
  clinicalSynthesis: string;
  /** 2. Perguntas de Supervisão (vinculadas à abordagem). */
  supervisionQuestions: string[];
  /** 3. Análise de Supervisão Clínica IA (vinculada à abordagem). */
  clinicalSupervision: string;
  /** 4. Plano para a Próxima Sessão (vinculado à abordagem). */
  nextSessionPlan: string[];
  /** As 4 seções em texto único numerado (cópia / registro). */
  supervision: string;
  disclaimer: string;
};

/**
 * Supervisão clínica completa da sessão, na linha teórica escolhida.
 * Roda no backend (chave da IA protegida, consentimento LGPD e auditoria).
 */
export async function superviseSession(
  patientId: string,
  sessionText: string,
  approach: string,
): Promise<SupervisionApiResponse> {
  return api.post<SupervisionApiResponse>('/ai/supervise-session', {
    patientId,
    sessionText,
    approach,
  });
}

export async function chatWithAi(
  patientId: string,
  question: string,
  sessionContext?: string,
): Promise<ChatApiResponse> {
  return api.post<ChatApiResponse>('/ai/chat', { patientId, question, sessionContext });
}

export async function interpretCycle(payload: {
  patientId: string;
  approach: string;
  date: string;
  subjectiveDistress: number;
  functionalImpairment: number;
  clinicalNotes?: string;
  indicators: Array<{ name: string; axis: string; score: number }>;
  previousCycleSummary?: string;
}): Promise<CycleInterpretationResponse> {
  return api.post<CycleInterpretationResponse>('/ai/interpret-cycle', payload);
}
